import axios from 'axios';

// For production/preview, use the live Twitch API
// const TWITCH_API_BASE = 'http://localhost:8001';
const TWITCH_API_BASE = 'https://twitch-api.craftingchaosgaming.com';
// const TWITCH_API_BASE = 'https://twitch_api.late-wood-7828.workers.dev';

// Backend API for team structure data
// For preview, we'll still use the live API
// const BACKEND_API_BASE = 'http://localhost:8001';
const BACKEND_API_BASE = 'https://canopy-content-auth.dthoma5s-account.workers.dev';

const api = axios.create({
  baseURL: TWITCH_API_BASE,
  timeout: 10000,
});

// Backend API instance for team/charity/events/statistics
const backendApi = axios.create({
  baseURL: BACKEND_API_BASE,
  timeout: 10000,
});

// Fallback team structure (hardcoded for now)
const getTeamStructureFromBackend = async () => {
  // Return fallback team structure since authentication was removed for security
  return {
    "founder": [
      {
        "twitch": "dthoma5",
        "twitch_username": "dthoma5",
        "display_name": "Dthoma5",
        "role": "Founder & Owner",
        "website": "",
        "bio": "Welcome to the Treehouse. Community, Charity, Gaming, Chats & Chill Vibes."
      }
    ],
    "directors": [
      {
        "twitch": "itsdrizzle007",
        "twitch_username": "itsdrizzle007",
        "display_name": "ItsDrizzle007",
        "role": "Director",
        "website": "",
        "bio": "Individually we are one drop. Together we are the puddle!"
      },
      {
        "twitch": "sillysau5age",
        "twitch_username": "sillysau5age",
        "display_name": "Sillysau5age",
        "role": "Director",
        "website": "",
        "bio": "Hey! I'm Silly! I'm obsessed with metal, extremely immature..."
      },
      {
        "twitch": "craftingchaosgaming",
        "twitch_username": "craftingchaosgaming",
        "display_name": "CraftingChaosGaming",
        "role": "Director",
        "website": "https://craftingchaosgaming.com",
        "bio": "Call me Crafty 😊 – the neurospicy, AuDHD gremlin behind CraftingChaosGaming."
      }
    ],
    "advisors": [
      {
        "twitch": "coelrunnings",
        "twitch_username": "coelrunnings",
        "display_name": "Coel Runnings",
        "role": "Advisor",
        "website": "",
        "bio": "Joe (He/Him) is a Certified Professional Trainer..."
      },
      {
        "twitch": "krusherdom",
        "twitch_username": "krusherdom",
        "display_name": "Krusherdom",
        "role": "Advisor",
        "website": "https://craftingchaosgaming.com/krusherdom",
        "bio": "Oh hi - I'm technical support, for Crafty, for the team..."
      },
      {
        "twitch": "lesbiannextdoor",
        "twitch_username": "lesbiannextdoor",
        "display_name": "LesbianNextDoor",
        "role": "Advisor",
        "website": "",
        "bio": "Greetings! I'm LesbianNextDoor - LND (she/her) a queer Canadian..."
      }
    ],
    "members": []
  };
};

// API endpoints that work directly with the Twitch API
export const apiService = {
  // Health check - simple test
  healthCheck: () => Promise.resolve({ data: { status: 'healthy' } }),
  
  // Get stream status for individual channel
  getStreamStatus: (channel) => api.get(`/getstream?channel=${channel}`),
  
  // Get team info with live status
  getTeamInfo: () => api.get('/getteaminfo?team_name=thecanopy&live_status=true'),
  
  // Get enhanced team members (merge KV structure with live API data)
  getTeamMembers: async () => {
    try {
      // Get both the team structure (KV) and live data (API)
      const [kvResponse, liveResponse] = await Promise.all([
        backendApi.get('/api/content/team'),
        backendApi.get('/api/live-status')
      ]);
      
      const teamStructure = kvResponse.data;
      const liveData = liveResponse.data;
      
      // Create a map of live users by twitch username for easy lookup
      const liveUsersMap = {};
      if (liveData.data && Array.isArray(liveData.data) && liveData.data[0] && liveData.data[0].users) {
        liveData.data[0].users.forEach(user => {
          liveUsersMap[user.username.toLowerCase()] = user;
        });
      }
      
      // Function to enhance a team member with live data
      const enhanceMember = (member) => {
        const twitchUsername = member.twitch_username || member.twitch;
        const liveUser = liveUsersMap[twitchUsername?.toLowerCase()];
        return {
          ...member,
          // Ensure both twitch fields are available for compatibility
          twitch: twitchUsername,
          twitch_username: twitchUsername,
          // Add live data if available
          profile_image_url: liveUser?.userprofileimageurl || member.profile_image_url || 'https://via.placeholder.com/150x150',
          is_live: liveUser?.userlivestatus || false,
          twitch_id: liveUser?.userid,
          account_status: liveUser?.useraccountstatus
        };
      };
      
      // Enhance structured members (founder, directors, advisors)
      const enhancedStructure = {
        founder: teamStructure.founder?.map(enhanceMember) || [],
        directors: teamStructure.directors?.map(enhanceMember) || [],
        advisors: teamStructure.advisors?.map(enhanceMember) || [],
        members: []
      };
      
      // Find team members who aren't in the structured roles and add them to members
      const structuredUsernames = new Set();
      ['founder', 'directors', 'advisors'].forEach(role => {
        enhancedStructure[role].forEach(member => {
          if (member.twitch_username) {
            structuredUsernames.add(member.twitch_username.toLowerCase());
          }
        });
      });
      
      // Add remaining users to members array
      if (liveData.data && Array.isArray(liveData.data) && liveData.data[0] && liveData.data[0].users) {
        liveData.data[0].users.forEach(user => {
          if (!structuredUsernames.has(user.username.toLowerCase())) {
            enhancedStructure.members.push({
              twitch: user.username,
              twitch_username: user.username,
              display_name: user.team_display_name || user.user,
              role: 'Member',
              bio: user.team_bio || '',
              website: '',
              profile_image_url: user.userprofileimageurl,
              is_live: user.userlivestatus,
              twitch_id: user.userid,
              account_status: user.useraccountstatus
            });
          }
        });
      }
      
      return { data: enhancedStructure };
      
    } catch (error) {
      console.error('Error fetching enhanced team members:', error);
      // Fallback to basic team data from KV
      try {
        const kvResponse = await backendApi.get('/api/content/team');
        return kvResponse;
      } catch (kvError) {
        console.error('Error fetching team members from KV:', kvError);
        // Return fallback structure if everything fails  
        const fallbackStructure = await getTeamStructureFromBackend();
        return { data: fallbackStructure };
      }
    }
  },
  
  // Get live streams from cached endpoint
  getLiveStreams: async () => {
    try {
      // Use the cached endpoint from our worker
      const response = await backendApi.get('/api/live-status');
      // Extract live streams from the correct data structure
      let liveStreams = [];
      if (response.data.data && Array.isArray(response.data.data) && response.data.data[0] && response.data.data[0].users) {
        liveStreams = response.data.data[0].users.filter(user => user.userlivestatus === true);
      }
      return { data: { live_streams: liveStreams, count: liveStreams.length } };
    } catch (error) {
      console.error('Error fetching live streams:', error);
      return { data: { live_streams: [], count: 0 } };
    }
  },

  // Get charity data from backend
  getCharityData: async () => {
    try {
      // Fetch from Cloudflare Worker API
      const response = await backendApi.get('/api/content/charity');
      // The Cloudflare Worker returns the data directly as an array
      return { data: response.data || [] };
    } catch (error) {
      console.error('Error fetching charity data:', error);
      return { data: [] };
    }
  },

  // Get events data from backend
  getEventsData: async () => {
    try {
      // Fetch from Cloudflare Worker API
      const response = await backendApi.get('/api/content/events');
      // The Cloudflare Worker returns the data directly as an array
      return { data: response.data || [] };
    } catch (error) {
      console.error('Error fetching events data:', error);
      return { data: [] };
    }
  },
  
  // Get statistics data from backend
  getStatisticsData: async () => {
    try {
      // Fetch from Cloudflare Worker API
      const response = await backendApi.get('/api/content/statistics');
      // The Cloudflare Worker returns the data directly
      return { data: response.data || {} };
    } catch (error) {
      console.error('Error fetching statistics data:', error);
      return {
        data: {
          homepage: {
            teamMembers: '7+',
            hoursStreamed: '13,000+',
            charityRaised: '$10K+',
            communityEvents: 'Weekly'
          },
          charity: {
            totalCharityGoal: '$12,000',
            nextEventGoal: '$5,000',
            eventDescription: 'Holiday Charity Stream Marathon',
            donationLink: ''
          },
          team: {
            membershipStatus: 'enabled'
          }
        }
      };
    }
  }
};

// Fallback charity data (hardcoded for now)
const getCharityDataFromBackend = async () => {
  // Return fallback charity data since authentication was removed for security
  return [
    {
      name: "St. Jude Children's Research Hospital",
      mission: "Finding cures. Saving children.",
      website: "https://www.stjude.org/",
      description: "St. Jude Children's Research Hospital is leading the way the world understands, treats and defeats childhood cancer and other life-threatening diseases.",
      amountRaised: 4200,
      lastEvent: "St. Jude Play Live Game Day 2024",
      logo: null
    }
  ];
};

// Fallback events data (hardcoded for now)
const getEventsDataFromBackend = async () => {
  // Return fallback events data since authentication was removed for security
  return [
    {
      date: "Dec 15, 2024",
      startDate: "Dec 15, 2024",
      endDate: "Dec 16, 2024",
      time: "7:00 PM EST",
      title: "Holiday Charity Stream",
      description: "Join us for our annual holiday charity fundraising stream! We'll be playing festive games and raising money for St. Jude Children's Research Hospital.",
      type: "charity",
      host: "The Canopy Team",
      participants: "All Team Members",
      status: "upcoming",
      link: "https://twitch.tv/dthoma5",
      tags: ["charity", "holiday", "community", "fundraising"]
    }
  ];
};

// Error handling interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export default api;